
import { h, Fragment } from 'preact'; /** @jsx h */
import { useState, useEffect } from 'preact/hooks';

import { ROUTES } from '@pyqml/shared';

import { useAuth } from "../app/AuthProvider";
import { Layout } from '../components/Layout';
import { Subheader } from '../components/Subheader';
import { Price } from '../components/Price';
import { Session } from '@pyqml/model';
import { PYQML } from '@pyqml/core';
import { PayModeSelect } from '../glue/PayModeSelect';


const SelectUpgradeModal = ({onClose, refetch}) => {
    
    //const { cacheableQuery, cacheablePromise } = useCacheableQueryProvider();
    const { subscriptionLevel, subscriptionDuration, nextDue, subscriptionState, authQuery, authPromise } = useAuth();

    
    const upgradeSubscription = (level: Session.SubscriptionLevel, payMode: Session.SubscriptionDuration) => {
        authPromise?.({
            method: "POST",
            endpoint: ROUTES.CLOUDAPI_PRIVATE_UPGRADE_SUBSCRIPTION,
            queryData: {
                level,
                payMode
            }
        }).then((res) => {
            onClose();
            refetch();
        });
    }

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const modal = document.querySelector('.modal-dialog');
            if (modal && !modal.contains(event.target as Node)) {
                onClose();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);


    return <PayModeSelect>{
        (Selector, payMode) => (
            <div className="modal-dialog modal-dialog-centered" role="document">
                <div className="modal-content" style={{ overflowY: "auto", maxHeight: "calc(100vh - 210px)" }}>
                    <div className={`modal-header border-0 bg-${subscriptionLevel}`}>
                        <h5 className="font-weight-bold" style={{ marginBottom: 0, fontFamily: "ShadowsIntoLight"}}>Upgrade your subscription</h5>
                        <button type="button" className="close" data-dismiss="modal" aria-label="Close" onClick={() => onClose()}>
                        <span aria-hidden="true">×</span>
                        </button>
                    </div>
                    <div className="modal-body">
                        <div className="pb-4 pr-4 pl-4 text-center">
                            <h2>Select Your New Plan</h2>
                            <p>
                                When you upgrade your subscription, we calculate the difference between your current plan and the new plan and
                                charge the account you used for the original registration.
                            </p>                                        
                            {
                                // only show the selector if the subscription is not already at the highest level
                                subscriptionDuration == Session.SubscriptionDuration.month && (

                                    // when the subscription is monthly but we can't upgrade the level
                                    Session.possibleLevelUpgrades(subscriptionLevel).length > 0
                                    ? <Selector />
                                    : <Fragment>
                                        <Subheader icon="fas fa-bolt" iconColor={subscriptionLevel} title={PYQML.PLANS[subscriptionLevel].name} tight />
                                        <Price payMode={payMode} year={PYQML.PLANS[subscriptionLevel]["year"]["pricePerMonth"]} month={PYQML.PLANS[subscriptionLevel]["month"]["pricePerMonth"]}/>
                                        <p className="btn-cta">
                                            <a href="#"
                                                className={`btn btn-${subscriptionLevel}`}
                                                onClick={() => upgradeSubscription(
                                                    subscriptionLevel,
                                                    Session.SubscriptionDuration.year
                                                )
                                                }>Upgrade to annual period now<br/><small>Your account will be charged!</small></a>
                                        </p>
                                    </Fragment>
                                )                        
                            }
                            
                            {
                                // by flipping required and existing, we look for the highest level that is not yet reached
                                Session.possibleLevelUpgrades(subscriptionLevel).map((level) => {
                                    return <Fragment>
                                        <Subheader icon="fas fa-bolt" iconColor={level} title={PYQML.PLANS[level].name} tight />
                                        <Price payMode={payMode} year={PYQML.PLANS[level]["year"]["pricePerMonth"]} month={PYQML.PLANS[level]["month"]["pricePerMonth"]}/>
                                        <p className="btn-cta">
                                            <a href="#"
                                                className={`btn btn-${level}`}
                                                onClick={() => upgradeSubscription(
                                                    level,
                                                    subscriptionDuration == Session.SubscriptionDuration.month
                                                        ? payMode
                                                        : Session.SubscriptionDuration.year
                                                    )
                                                }>Upgrade to {PYQML.PLANS[level].name} now<br/><small>Your account will be charged!</small></a>
                                        </p>
                                    </Fragment>
                                })
                                                       
                            }
                        </div>
                    </div>
                </div>
            </div>
        )
    }</PayModeSelect>
}

export const Settings = (props) => {
    
    const { subscriptionLevel, subscriptionDuration, nextDue, subscriptionState, authQuery, authPromise, refetchAuth } = useAuth();
    const [showModal, setShowModal] = useState<boolean>(false);


    const [{data, error, loading}, refetch, count] = authQuery?.({
        method: "POST",
        endpoint: ROUTES.CLOUDAPI_PRIVATE_GET_EMAIL_SUBSCRIPTION,
    });

    const subscribe = () => {
        
        authPromise?.({
            method: "POST",
            endpoint: ROUTES.CLOUDAPI_PRIVATE_SUBSCRIBE_BULLETIN,
        }).then((res) => {
            refetch();
        });
    };

    const unsubscribe = () => {
        
        authPromise?.({
            method: "GET",
            endpoint: `${ROUTES.CLOUDAPI_PUBLIC_UNSUBSCRIBE_EMAIL}?id=${data[0]?.subscriptionHash}`,
        }).then((res) => {
            refetch();
            
        });
    };

    const cancelSubscription = () => {
        authPromise?.({
            method: "POST",
            endpoint: ROUTES.CLOUDAPI_PRIVATE_CANCEL_SUBSCRIPTION,
        }).then((res) => {
            refetchAuth?.();
        });
    };



    return <Fragment>
        { showModal &&
            <div
                style={{
                    position: "fixed",
                    color: "white",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    backgroundColor: "rgba(0,0,0,0.5)",
                    zIndex: 1000
                    }}>
                <SelectUpgradeModal onClose={()=>setShowModal(false)} refetch={refetchAuth}/>
            </div>        
        }
        { subscriptionLevel && <Layout.Stacked>
            <Layout.Section weight="equal">
                <h1 className="secondfont mb-3 font-weight-bold">
                Settings
                </h1>
                <hr/>
                

                <Subheader title="Your Account" />
                
                <div className="d-flex justify-content-between" style={{maxWidth: "400px"}}>
                    <div>
                        Your current <strong>plan</strong>:
                    </div>
                    <div>
                        <code>{PYQML.PLANS[subscriptionLevel].name}</code>
                    </div>
                </div>

                <div className="d-flex justify-content-between" style={{maxWidth: "400px"}}>
                    <div>
                        Payment <strong>frequency</strong>:
                    </div>
                    <div>
                        <code>{subscriptionDuration === 'month' ? "monthly" : "annually"}</code>
                    </div>
                </div>

                <div className="d-flex justify-content-between" style={{maxWidth: "400px"}}>
                    <div>
                        { subscriptionState == Session.SubscriptionState.active ? "Next payment due:" : "Canceled as of (no further payments)" }
                    </div>
                    <div>
                        <code>{nextDue}</code>
                    </div>
                </div>


                <div className="d-flex justify-content-between" style={{maxWidth: "400px"}}>
                    <div></div>
                    <div>
                        { 
                            (
                                Session.possibleLevelUpgrades(subscriptionLevel).length > 0
                                || subscriptionDuration == Session.SubscriptionDuration.month
                            ) && <b><a href="#" onClick={() => setShowModal(true)}>Upgrade</a> </b> }
                        { subscriptionState == Session.SubscriptionState.active ? <Fragment>| <a href="#" onClick={cancelSubscription}>Cancel</a></Fragment> : "" }
                    </div>
                </div>

                <hr/>
                
                
                <Subheader title="Active Email Subscriptions" />


                
                <div className="d-flex justify-content-between" style={{maxWidth: "400px"}}>
                    <div>
                        Your current <strong>email subscription</strong>:
                    </div>
                    <div>
                        <code>{ data ? (data?.length > 0 ? data[0]?.sequenceId ?? "" : "None") : ""}</code>
                    </div>
                </div>

                <div className="d-flex justify-content-between" style={{maxWidth: "400px"}}>
                    <div>
                        Email <strong>frequency</strong>:
                    </div>
                    <div>
                        <code>{ data ? (data?.length > 0 ? data[0]?.frequency ?? "" : "---") : ""}</code>
                    </div>
                </div>


                <div className="d-flex justify-content-between" style={{maxWidth: "400px"}}>
                    <div></div>
                    <div>
                        { data ? (data?.length > 0 ? <a href="#"  onClick={unsubscribe}>Cancel</a> ?? "" : <b><a href="#" onClick={subscribe}>Subscribe to the Bulletin</a></b>) : ""}
                        
                    </div>
                </div>


            </Layout.Section>
        </Layout.Stacked> }
    </Fragment>
}
  
